import { createSignal, createUniqueId, type JSX, Show } from "solid-js";

/**
 * `bool:` è un namespace JSX di Solid, ma la sua lista di chiavi tipizzate
 * (`ExplicitBoolAttributes`) nasce vuota: senza questa dichiarazione
 * `bool:inert` non compila. Sta qui, accanto al suo unico uso, perché è la sola
 * ragione per cui esiste.
 */
declare module "solid-js" {
  namespace JSX {
    interface ExplicitBoolAttributes {
      inert: boolean;
    }
  }
}

type AccordionSectionProps = {
  /** Classe Phosphor della sola icona di sezione, es. `ph-buildings`. */
  icon: string;
  title: string;
  /** Riassunto della sezione, mostrato aperta o chiusa. Assente = niente hint. */
  hint?: string;
  defaultOpen?: boolean;
  children: JSX.Element;
};

/**
 * Una sezione ad apertura indipendente della sub-sheet «Informazioni azienda».
 *
 * Disclosure scritto a mano, non `Accordion`/`Collapsible` di Kobalte: quelli
 * animano con keyframes, che non sono interrompibili (riaprire durante la
 * chiusura riparte da zero) e non offrono un pannello inerte. Qui servono
 * entrambe le cose.
 *
 * Il contenuto del pannello è **sempre montato**: un `<Show>` lo smonterebbe
 * all'istante del click e la transizione di chiusura non si vedrebbe mai. Il
 * pannello chiuso è quindi alto zero *e* `inert`, altrimenti i suoi link
 * resterebbero raggiungibili con Tab e leggibili dal cursore virtuale in una
 * sezione che per tutti gli altri è chiusa.
 */
export const AccordionSection = (props: AccordionSectionProps) => {
  // `defaultOpen` è letto una volta sola, come valore iniziale: da qui in poi
  // la sezione appartiene a chi la apre e la chiude, e riaprirla perché il
  // padre ha ricalcolato la prop sarebbe un cambio di stato che nessuno ha
  // chiesto.
  const [open, setOpen] = createSignal(props.defaultOpen ?? false);
  const panelId = createUniqueId();

  let triggerEl: HTMLButtonElement | undefined;
  let panelEl: HTMLDivElement | undefined;

  const toggle = () => {
    // In chiusura il focus può trovarsi dentro il pannello: applicargli `inert`
    // mentre lo tiene lo scaricherebbe su `<body>`, e chi naviga da tastiera
    // ripartirebbe dall'inizio della pagina. Va riportato sul trigger prima.
    //
    // `preventScroll` perché `focus()` fa uno scroll-into-view implicito: senza,
    // chiudere una sezione lunga potrebbe far saltare la sub-sheet al trigger
    // mentre il pannello si sta ancora ritirando — due movimenti insieme, uno
    // dei quali nessuno ha chiesto (D7). Il trigger è quasi sempre già in vista,
    // quindi la riga è una garanzia, non una correzione visibile.
    if (open() && panelEl?.contains(document.activeElement)) {
      triggerEl?.focus({ preventScroll: true });
    }
    setOpen((isOpen) => !isOpen);
  };

  return (
    <div class="border-b">
      <h3>
        <button
          ref={triggerEl}
          type="button"
          class="flex w-full cursor-pointer items-center gap-3 py-4 text-left focus-visible:outline-hidden focus-visible:ring-[1.5px] focus-visible:ring-ring"
          aria-expanded={open()}
          aria-controls={panelId}
          onClick={toggle}
        >
          <i
            class={`ph ${props.icon} shrink-0 text-lg text-primary`}
            aria-hidden="true"
          />
          <span class="shrink-0 font-medium text-base">{props.title}</span>
          {/*
            L'hint sta dentro il bottone perché è informazione, non decorazione:
            uno screen reader lo legge dopo il titolo. Ed è renderizzato anche a
            sezione aperta — farlo sparire all'apertura sposterebbe il layout
            dell'intestazione proprio durante l'espansione.

            `company_size` è testo libero e può essere lunghissimo: l'hint
            prende lo spazio che avanza (`flex-1` su base 0) e lo eccedente lo
            tronca, così il chevron non esce mai dall'intestazione.
          */}
          <Show when={props.hint}>
            {(hint) => (
              <span class="min-w-0 flex-1 truncate text-right text-muted-foreground text-xs">
                {hint()}
              </span>
            )}
          </Show>
          <i
            class="ph ph-caret-down ml-auto shrink-0 text-muted-foreground transition-[rotate] duration-200 ease-out-strong motion-reduce:transition-none"
            classList={{ "rotate-180": open() }}
            aria-hidden="true"
          />
        </button>
      </h3>

      {/*
        Eccezione registrata al non negoziabile «anima solo transform/opacity»,
        la stessa già in produzione in `job-row.tsx`: l'altezza del pannello
        dipende dal contenuto e non è nota in anticipo, e la coppia
        `grid-template-rows` + `overflow-hidden` è la sola tecnica che la
        interpola senza misurarla in JS. Non è un percorso caldo — una sezione
        per volta, su gesto esplicito.

        È una `transition`, non dei keyframes: chiudere e riaprire di fretta
        riparte dal punto in cui si trova.
      */}
      <div
        ref={panelEl}
        id={panelId}
        class="grid transition-[grid-template-rows] duration-200 ease-out-strong motion-reduce:transition-none"
        style={{ "grid-template-rows": open() ? "1fr" : "0fr" }}
        // `bool:` e non `inert={…}` liscio: la forma liscia compila in
        // `elem.inert = value`, una proprietà che jsdom non implementa, quindi
        // l'attributo non comparirebbe mai — nel DOM di test come nella
        // asserzione che lo cerca. `bool:` fa un `setAttribute` vero.
        bool:inert={!open()}
      >
        <div class="overflow-hidden">
          {/* La spaziatura sta dentro alla parte che collassa: fuori
              lascerebbe un vuoto sotto all'intestazione a sezione chiusa. */}
          <div class="pb-4">{props.children}</div>
        </div>
      </div>
    </div>
  );
};
